import { Link } from "react-router-dom";
import { DocumentTypeIcon } from "@/components/knowledge-base/DocumentTypeIcon";
import { Skeleton } from "@/components/ui/skeleton";
import { ConsoleBand, KnowledgeBandData } from "@/types/dashboard/genie-console";
import { EmptyPreview, PreviewError } from "./previewShell";

interface KnowledgePreviewProps {
  band: ConsoleBand<KnowledgeBandData>;
  brandColor: string;
  genieId: string | null;
}

/**
 * BRAIN band preview — the user's ACTUAL documents, not an illustration.
 * Real names, real source-type icons, real folders, real last-updated dates.
 */
export const KnowledgePreview = ({ band, brandColor, genieId }: KnowledgePreviewProps) => {
  if (band.isError) return <PreviewError label="knowledge" />;

  if (band.isLoading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2].map((row) => (
          <Skeleton key={row} className="h-11 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  const { documents, documentCount, staleDocuments } = band.data;

  if (documents.length === 0) {
    return (
      <EmptyPreview
        message="Nothing in the knowledge base yet — your Genie is answering from its prompt alone."
        actionLabel="Add your first document"
        to={genieId ? `/genie-hub/knowledge-base/${genieId}` : "/genie-hub/knowledge-base"}
        brandColor={brandColor}
      />
    );
  }

  const staleIds = new Set(staleDocuments.map((document) => document.id));

  return (
    <div className="space-y-2">
      {documents.map((document) => (
        <Link
          key={document.id}
          to={genieId ? `/genie-hub/knowledge-base/${genieId}` : "/genie-hub/knowledge-base"}
          className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white px-3 py-2.5 transition-colors hover:border-gray-200 hover:bg-[#F9FAFC]"
        >
          <span
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg"
            style={{ backgroundColor: `${brandColor}14` }}
          >
            <DocumentTypeIcon document={document} showTooltip={false} iconSize="sm" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-gray-900">
              {document.name}
            </span>
            <span className="block truncate text-xs text-gray-500">
              {document.folder?.name ? `${document.folder.name} · ` : ""}
              {document.dependent_agents?.length
                ? `${document.dependent_agents.length} ${
                    document.dependent_agents.length === 1 ? "Genie" : "Genies"
                  }`
                : "Not attached"}
            </span>
          </span>
          {staleIds.has(document.id) && (
            <span className="flex-shrink-0 rounded-full bg-genie-accent/15 px-2 py-0.5 text-[11px] font-bold text-genie-accent-dark">
              Ageing
            </span>
          )}
        </Link>
      ))}

      {documentCount > documents.length && (
        <p className="pt-1 text-xs text-gray-500">
          + {documentCount - documents.length} more in the knowledge base
        </p>
      )}
    </div>
  );
};

export default KnowledgePreview;
